import type { PropertyHomeOptions } from './usePropertyHomeData';
import type { InsightKind } from './types';

/* Every research snapshot is one JSON blob; the property used to download all
 * 13 (3.75 MB for MyHealthyDish) on every visit, whatever the tab. Each tab
 * now asks for what it renders.
 *
 * `audience_comments` comes as a summary without its items (~10 KB instead of
 * ~1.5 MB, see `SUMMARY_KINDS`); screens that list comments page them on
 * demand. `tagged_posts` carried base64 thumbnails inline (~1.35 MB); the
 * backend now stores them as URLs. Where a tab can render without them they
 * are deferred: a separate request in parallel, so the rest of the tab does
 * not wait for them. */

export type PropertyTabInsights = PropertyHomeOptions & {
  kinds: InsightKind[];
};

/* The tab strip hides Campaigns when there are no demo campaigns, and the
 * idea-campaign provider reads the ideas, whichever tab is open. */
const ALWAYS: InsightKind[] = ['opportunities'];

const TABS: Record<string, PropertyTabInsights> = {
  overview: {
    kinds: [
      ...ALWAYS,
      'channel_history', // channel section, metric strip, history evidence
      'market_formats', // market glance and its evidence
      // Creator finder
      'audience_overlap',
      'audience_interests',
      'competitors',
      // "What your audience says" (counts and themes from the summary) and
      // "Creators talking about you".
      'audience_comments',
      'tagged_posts',
    ],
    deferred: ['audience_comments', 'tagged_posts'],
    growth: true,
  },
  // Tagged posts only supply thumbnails in the idea evidence panel.
  ideas: {
    kinds: [...ALWAYS, 'tagged_posts'],
    deferred: ['tagged_posts'],
    growth: false,
  },
  campaigns: {
    kinds: [...ALWAYS, 'tagged_posts'],
    deferred: ['tagged_posts'],
    growth: false,
  },
  // Series reads its own API; no channel research or growth is rendered here.
  series: { kinds: [], growth: false },
  market: {
    kinds: [
      ...ALWAYS,
      'market_formats',
      'market_topics',
      'commercial',
      'business_competitors',
    ],
    growth: false,
  },
  audience: {
    kinds: [
      ...ALWAYS,
      'audience_overlap',
      'audience_interests',
      'audience_sentiment',
      'audience_comments',
    ],
    growth: false,
  },
  // Mentions: tagged posts join the accepted content.
  content: {
    kinds: [...ALWAYS, 'tagged_posts', 'mention_discovery'],
    deferred: ['tagged_posts'],
    growth: false,
  },
};

const OTHER: PropertyTabInsights = { kinds: ALWAYS, growth: false };

/** The snapshots (and whether growth) a property tab needs. */
export const propertyTabInsights = (tab: string): PropertyTabInsights =>
  TABS[tab] || OTHER;
